import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Home,
  Target, 
  Settings, 
  BookOpen,
  X,
  FileText,
  Building2,
  Search
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useTheme } from '@/contexts/ThemeContext';
import logo from '@/logo.svg';

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Research', href: '/research', icon: BookOpen },
  { name: 'Clusters', href: '/cluster', icon: Target },
  { name: 'Reports', href: '/reports', icon: FileText },
  { name: 'AI Audit', href: '/ai-audit', icon: Search },
  // { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  // { name: 'Users', href: '/users', icon: Users },
  // { name: 'Scout', href: '/scout', icon: Search },
  { name: 'Settings', href: '/settings', icon: Settings },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose, isMobile = false }) => {
  const location = useLocation();
  const { theme } = useTheme();
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  console.log(theme);

  const toggleOrgMenu = () => {
    setShowOrgMenu(!showOrgMenu);
  };

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsLoadingOrgs(true);
      try {
        const response = await apiService.get('/organization/user-organizations/');
        const orgs = response.data?.results || response.data || [];
        // Ensure orgs is always an array
        const organizationsArray = Array.isArray(orgs) ? orgs : [];
        setOrganizations(organizationsArray);
        if (organizationsArray.length > 0) {
          setCurrentOrg(organizationsArray[0]); // Set first org as current
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        // Set empty array on error to prevent map errors
        setOrganizations([]);
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleOrgSwitch = (org: Organization) => {
    setCurrentOrg(org);
    setShowOrgMenu(false);
    // Here you would typically update the global state or context
    // to reflect the organization change across the app
  };

  if (isMobile) {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 lg:hidden">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="fixed left-4 top-4 h-auto w-32 bg-card border rounded-lg z-50">
          <div className="flex flex-col">
            <div className="flex flex-col items-center py-4 px-2 relative">
              <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-indigo-400 to-fuchsia-500 flex items-center justify-center mb-2">
                <img src={logo} alt="Serpware Logo" className="w-5 h-5" />
              </div>
              <div className="text-center">
                <h1 className="text-sm font-semibold leading-tight">Serpware</h1>
                <p className="text-xs text-muted-foreground leading-tight">Scout</p>
              </div>
              
              {/* Organization Selector */}
              <div className="w-full relative mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full flex items-center justify-center p-2 hover:bg-muted/50"
                  onClick={toggleOrgMenu}
                >
                  <div className="flex flex-col items-center space-y-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="text-center">
                      <div className="text-xs font-medium leading-tight truncate max-w-[80px]">
                        {isLoadingOrgs ? 'Loading...' : currentOrg?.name || 'No Org'}
                      </div>
                    </div>
                  </div>
                </Button>
                
                {/* Organization Dropdown */}
                {showOrgMenu && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50">
                    <div className="p-2 space-y-1">
                      {Array.isArray(organizations) && organizations.map((org) => (
                        <Button
                          key={org.id}
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start p-2 text-xs"
                          onClick={() => handleOrgSwitch(org)}
                        >
                          <Building2 className="h-3 w-3 mr-2" />
                          <span className="truncate">{org.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onClose}
                className="absolute top-2 right-2 h-6 w-6 p-0"
              >
                <X className="h-3 w-3" style={{ color: 'var(--color-indigo-300)' }} />
              </Button>
            </div>
            <nav className="px-2 py-4 space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex flex-col items-center px-2 py-3 text-xs font-medium rounded-md transition-colors ${
                      isActive 
                        ? 'bg-accent text-accent-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                    style={{
                      '--icon-color': 'var(--color-indigo-300)'
                    } as React.CSSProperties}
                    onClick={onClose}
                  >
                    <Icon className="h-6 w-6 mb-1" style={{ color: 'var(--icon-color)' }} />
                    <span className="text-center leading-tight">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
            
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hidden lg:block w-32">
      <Card className="h-screen">
        <div className="flex flex-col items-center py-4 px-2">
          <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-indigo-400 to-fuchsia-500 flex items-center justify-center mb-2">
            <img src={logo} alt="Serpware Logo" className="w-5 h-5" />
          </div>
          <div className="text-center">
            <h1 className="text-sm font-semibold leading-tight">Serpware</h1>
            <p className="text-xs text-muted-foreground leading-tight">Scout</p>
          </div>
          
          {/* Organization Selector */}
          <div className="w-full relative mt-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full flex items-center justify-center p-2 hover:bg-muted/50"
              onClick={toggleOrgMenu}
            >
              <div className="flex flex-col items-center space-y-1">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div className="text-center">
                  <div className="text-xs font-medium leading-tight truncate max-w-[80px]">
                    {isLoadingOrgs ? 'Loading...' : currentOrg?.name || 'No Org'}
                  </div>
                </div>
              </div>
            </Button>
            
            {/* Organization Dropdown */}
            {showOrgMenu && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50">
                <div className="p-2 space-y-1">
                  {Array.isArray(organizations) && organizations.map((org) => (
                    <Button
                      key={org.id}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start p-2 text-xs"
                      onClick={() => handleOrgSwitch(org)}
                    >
                      <Building2 className="h-3 w-3 mr-2" />
                      <span className="truncate">{org.name}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <nav className="px-2 py-4 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex flex-col items-center px-2 py-3 text-xs font-medium rounded-md transition-colors ${
                  isActive 
                    ? 'bg-accent text-accent-foreground' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                style={{
                  '--icon-color': 'var(--color-indigo-300)'
                } as React.CSSProperties}
              >
                <Icon className="h-6 w-6 mb-1" style={{ color: 'var(--icon-color)' }} />
                <span className="text-center leading-tight">{item.name}</span>
              </Link>
            );
          })}
        </nav>
        
      </Card>
    </div>
  );
};

export default Sidebar; 